/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {Dayjs} from 'dayjs'
import Dates from 'utils/Dates'
import {BookingFilter} from 'types/app'
import {Timeslot} from 'types/entities/Timeslot'
import {OfferBasicInputs} from 'components/Offers/schemas/useOfferBasicSchema'
import {FieldErrors} from 'react-hook-form'
import {MAXIMUM_DEFAULT_VALIDITY_DAYS, OFFER_VALID_UNTIL_DAYS_BEFORE_EVENT} from 'helpers/config'

type OfferValidUntilDateInput = Date | string | Dayjs

const toDayStart = (value: OfferValidUntilDateInput): Dayjs => Dates.dayjs(value).startOf('day')

export const calculateOfferValidUntil = (
    eventDate: OfferValidUntilDateInput,
    referenceDate: OfferValidUntilDateInput = new Date()
): string => {
    const eventDay = toDayStart(eventDate)
    const referenceDay = toDayStart(referenceDate)

    const maxFromReference = referenceDay.add(MAXIMUM_DEFAULT_VALIDITY_DAYS, 'day')
    const maxBeforeEvent = eventDay.subtract(OFFER_VALID_UNTIL_DAYS_BEFORE_EVENT, 'day')

    const validUntil = maxFromReference.isBefore(maxBeforeEvent, 'day') ? maxFromReference : maxBeforeEvent

    return validUntil.format('YYYY-MM-DD')
}

interface BuildOfferBasicFormValuesParams {
    filter: BookingFilter
    defaultValues?: Partial<OfferBasicInputs>
    companyIdFromContext?: number | null
}

const getInitialDate = (date?: string) => {
    return date ? date : Dates.ymd(new Date())
}

const getInitialValidUntil = (date?: string) => calculateOfferValidUntil(getInitialDate(date))

export const buildOfferBasicFormInitialValues = ({
    filter,
    defaultValues,
    companyIdFromContext,
}: BuildOfferBasicFormValuesParams): Partial<OfferBasicInputs> => ({
    adults: filter.adults,
    children: filter.children,
    date: getInitialDate(filter.date),
    valid_until: getInitialValidUntil(filter.date),
    company_id: companyIdFromContext ?? undefined,
    probability: 50,
    ...(defaultValues ?? {}),
})

export const buildOfferBasicFormResetValues = ({
    filter,
    defaultValues,
    companyIdFromContext,
}: BuildOfferBasicFormValuesParams): Partial<OfferBasicInputs> => {
    const initialDate = defaultValues?.date ? defaultValues.date : getInitialDate(filter.date)
    const initialAdults = defaultValues?.adults !== undefined ? defaultValues.adults : filter.adults
    const initialChildren = defaultValues?.children !== undefined ? defaultValues.children : filter.children

    return {
        adults: initialAdults,
        children: initialChildren,
        date: initialDate,
        valid_until: defaultValues?.valid_until ?? getInitialValidUntil(initialDate),
        company_id: companyIdFromContext ?? undefined,
        probability: 50,
        contact_id: defaultValues?.contact_id,
        ...(defaultValues ?? {}),
    }
}

export const hasBookingFilterChanges = (filter: BookingFilter, data: Partial<BookingFilter>) => {
    return Object.keys(data).some((key) => {
        const typedKey = key as keyof BookingFilter
        return data[typedKey] !== filter[typedKey]
    })
}

export const toCounterValue = (value: string | number) => {
    return value === '' ? 0 : Number(value)
}

export const getTimeslotClock = (timeslot: Timeslot) => {
    const timeslotStart = new Date(timeslot.start_at)
    return {
        hour: timeslotStart.getHours(),
        minute: timeslotStart.getMinutes(),
    }
}

export const getFirstFormErrorMessage = (errors: FieldErrors<OfferBasicInputs>) => {
    const firstErrorKey = Object.keys(errors)[0] as keyof OfferBasicInputs | undefined
    if (!firstErrorKey) {
        return undefined
    }

    return errors[firstErrorKey]?.message
}

export const getSelectedTimeslot = (selectedTimeslot: Timeslot | null, selectedTimeslotLocal: Timeslot | null) => {
    return selectedTimeslot ?? selectedTimeslotLocal
}

export const hasAtLeastOnePerson = (values: OfferBasicInputs) => {
    return (values.adults ?? 0) + (values.children ?? 0) > 0
}
